import {
  OfferManager,
  RenderCardProps,
  SubscriptionsData
} from '@uma/react-commons/dist/index';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import React, { useCallback, useMemo } from 'react';
import MastiffOfferTag from 'src/components/MastiffOfferTag';
import { buildSubscriptionsPayload } from 'src/utils/buildSubscriptionsPayload';
import { Device } from 'src/types/IDevices';
import { DeviceTypeParam } from 'src/utils/enums';
import getDeviceImage from 'src/utils/getDeviceImage';
import {
  CardSection,
  ContentType,
  DeviceImage,
  DeviceInfo,
  DeviceName,
  Info,
  OfferTag
} from './styles';
import SkeletonLoading from '../SkeletonLoading';

type TDeviceCard = {
  item: Device;
  onClick: () => void;
  subscriptionsData?: SubscriptionsData;
  showOfferCardDevice?: boolean;
};

const DeviceCard = ({
  item,
  onClick,
  subscriptionsData,
  showOfferCardDevice
}: TDeviceCard) => {
  const { translate } = useDependencyManagerContext();
  const { images, identity, __typename, type } = item;

  const isPrinter = type === DeviceTypeParam.PRINTER;

  const subscriptions = useMemo(
    () => buildSubscriptionsPayload(item, subscriptionsData),
    [item, subscriptionsData]
  );

  const renderOfferCard = useCallback(
    ({ offers, onClick: onOfferClick }: RenderCardProps) => {
      const firstOffer = offers?.[0];
      if (!firstOffer) return <></>;
      return (
        <MastiffOfferTag
          offerText={firstOffer.hyperlinkText || ''}
          onClick={() => onOfferClick(firstOffer)}
        />
      );
    },
    []
  );

  const offerOverlay = useMemo(
    () => (
      <OfferTag>
        <OfferManager
          showOfferCard={isPrinter}
          modelNumber={identity?.makeAndModel?.number}
          serialNumber={identity?.serialNumber}
          subscriptions={subscriptions}
          renderCard={renderOfferCard}
          loadingComponent={<SkeletonLoading />}
        />
      </OfferTag>
    ),
    [
      isPrinter,
      identity?.makeAndModel?.number,
      identity?.serialNumber,
      subscriptions,
      renderOfferCard
    ]
  );

  return (
    <CardSection
      data-testid="device-card"
      background="filled"
      border="outlined"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <>
        {showOfferCardDevice && offerOverlay}
        <DeviceImage>{getDeviceImage(images, __typename)}</DeviceImage>
        <ContentType>
          <DeviceName
            {...(identity?.friendlyName && {
              'data-component': 'DevicesFriendlyNameValue'
            })}
            className="caption"
            data-testid="device-name"
          >
            {identity?.friendlyName || identity?.makeAndModel?.name}
          </DeviceName>
          <Info>
            <DeviceInfo className="caption-small">
              {identity?.makeAndModel?.name}
            </DeviceInfo>
            <DeviceInfo
              data-component="DevicesSerialNumberValue"
              className="caption-small"
            >
              {`${translate('devices.serialNumber', 'Serial number')}`}:{' '}
              {identity?.serialNumber}
            </DeviceInfo>
          </Info>
        </ContentType>
      </>
    </CardSection>
  );
};
export default DeviceCard;
